import React, { ReactElement } from "react";
import { Avatar, Text, Flex } from "@govlaunch/web";
import { FlexProps } from "@govlaunch/web/src/box/Flex";
import { IStoryFieldsFragment } from "../fragments/__generated__/StoryFieldsFragment.generated";

interface IStoryAuthorProps {
  story: Pick<IStoryFieldsFragment, "author">;
  cardTitle?: string;
  renderLinkWrap?: (story: Pick<IStoryFieldsFragment, "author">, children: ReactElement) => ReactElement;
  isSmall?: boolean;
}

export default function StoryAuthor({
  story,
  cardTitle,
  renderLinkWrap,
  isSmall = false,
  ...props
}: IStoryAuthorProps & FlexProps) {
  if (!story.author) {
    return null;
  }

  const children = isSmall ? (
    <StoryAuthorOneLineCard author={story.author as TCoauthor} {...props} fontSize={18} flexWrap="wrap" />
  ) : (
    <StoryAuthorCard author={story.author as TCoauthor} cardTitle={cardTitle} {...props} />
  );

  return renderLinkWrap ? renderLinkWrap(story, children) : children;
}

export type TCoauthor = {
  __typename: "GovernmentUser" | "VendorUser" | "PendingUser" | "Company";
  _id: any;
  firstName: string | null | undefined;
  slug: string;
  isSiteAdmin: boolean | null | undefined;
  isLimitedSiteAdmin: boolean | null | undefined;
  fullName: string | null | undefined;
  jobTitle: string | null | undefined;
  title: string | null | undefined;
  avatar: string | null | undefined;
  logo: string | null | undefined;
  name: string | null | undefined;
};

export function constructCoauthors(
  coauthors: IStoryFieldsFragment["coauthors"],
  vendorCoauthors: IStoryFieldsFragment["vendorCoauthors"],
): TCoauthor[] {
  const nonNullableCoauthors = coauthors || [];
  const nonNullableVendorCoauthors = vendorCoauthors || [];

  const parsedCoauthor = nonNullableCoauthors.map((coauthor) => parseTCoauthorFromCoauthor(coauthor));
  const parsedVendorCoauthors = nonNullableVendorCoauthors.map((vendorCoauthor) =>
    parseTCoauthorFromVendorCoauthor(vendorCoauthor),
  );

  return [...parsedVendorCoauthors, ...parsedCoauthor].filter(Boolean);
}

export function parseTCoauthorFromCoauthor(coauthor: NonNullable<IStoryFieldsFragment["coauthors"]>[0]): TCoauthor {
  if (coauthor.__typename === "VendorUser" || coauthor.__typename === "GovernmentUser") {
    return {
      __typename: coauthor.__typename,
      _id: coauthor._id,
      firstName: coauthor.firstName,
      slug: coauthor.slug,
      isSiteAdmin: coauthor.isSiteAdmin,
      isLimitedSiteAdmin: coauthor.isLimitedSiteAdmin,
      fullName: coauthor.fullName,
      jobTitle: coauthor.fullName,
      title: coauthor.title,
      avatar: coauthor.avatar,
      logo: coauthor.avatar,
      name: coauthor.fullName,
    };
  }

  return {
    __typename: coauthor.__typename,
    _id: coauthor._id,
    firstName: coauthor.firstName,
    slug: coauthor.slug,
    isSiteAdmin: coauthor.isSiteAdmin,
    isLimitedSiteAdmin: coauthor.isLimitedSiteAdmin,
    fullName: coauthor.fullName,
    jobTitle: coauthor.fullName,
    title: null,
    avatar: coauthor.avatar,
    logo: coauthor.avatar,
    name: coauthor.fullName,
  };
}

export function parseTCoauthorFromVendorCoauthor(
  vendorCoauthor: NonNullable<IStoryFieldsFragment["vendorCoauthors"]>[0],
): TCoauthor {
  return {
    __typename: vendorCoauthor.__typename,
    _id: vendorCoauthor._id,
    firstName: vendorCoauthor.name,
    slug: vendorCoauthor.slug,
    isSiteAdmin: null,
    isLimitedSiteAdmin: null,
    fullName: vendorCoauthor.name,
    jobTitle: null,
    title: null,
    avatar: vendorCoauthor.logo,
    logo: vendorCoauthor.logo,
    name: vendorCoauthor.name,
  };
}

interface IStoryAuthorCardProps {
  author: TCoauthor;
  cardTitle?: string;
  showDivider?: boolean;
}

export function StoryAuthorCard({
  author,
  cardTitle = "AUTHOR",
  showDivider = false,
  ...props
}: IStoryAuthorCardProps) {
  const authorAvatar =
    author.isSiteAdmin || author.isLimitedSiteAdmin
      ? "https://cdn.filestackcontent.com/jGYfT4X8QyGteXUmbjgM"
      : author.avatar;
  const authorName =
    author.isSiteAdmin || author.isLimitedSiteAdmin ? "Lindsay Pica-Alfano" : author.fullName || author.firstName;
  // eslint-disable-next-line
  const authorTitle =
    author.isSiteAdmin || author.isLimitedSiteAdmin
      ? "Executive Producer"
      : author.__typename !== "PendingUser"
      ? author.title
      : author.jobTitle;

  const card = (
    <Flex
      bg="teal.200"
      p={8}
      pb={4}
      direction="column"
      alignItems="center"
      {...getDividerProps(showDivider)}
      {...props}
    >
      <Avatar size="xl" src={authorAvatar} name={authorName} borderColor="blue.600" border="2px" />
      <Text color="gray.700" mt={4} mb={3} fontSize="md">
        {authorName}
      </Text>
      <Text color="gray.600" fontSize="sm" m={0} textAlign="center">
        {authorTitle}
      </Text>
      <Text color="blue.700" mt={2} mb={0} fontSize="sm" textTransform="uppercase" css={{ textOverflow: "ellipsis" }}>
        {cardTitle}
      </Text>
    </Flex>
  );

  return card;
}

interface IStoryAuthorOneLineCardProps {
  author: TCoauthor;
  cardTitle?: string;
  showDivider?: boolean;
  fontSize?: number;
  flexWrap?: "nowrap" | "wrap" | "wrap-reverse";
}

export function StoryAuthorOneLineCard({ author, fontSize = 18, flexWrap = "wrap" }: IStoryAuthorOneLineCardProps) {
  const authorAvatar =
    author.isSiteAdmin || author.isLimitedSiteAdmin
      ? "https://cdn.filestackcontent.com/jGYfT4X8QyGteXUmbjgM"
      : author.avatar;
  const authorName =
    author.isSiteAdmin || author.isLimitedSiteAdmin ? "Lindsay Pica-Alfano" : author.fullName || author.firstName;
  // eslint-disable-next-line
  const authorTitle =
    author.isSiteAdmin || author.isLimitedSiteAdmin
      ? "Executive Producer"
      : author.__typename !== "PendingUser"
      ? author.title
      : author.jobTitle;

  return (
    <div
      css={{
        display: "flex",
        flexDirection: "row",
        flexWrap: flexWrap,
        alignItems: "center",
        justifyContent: "flex-start",
      }}
    >
      <div css={{ marginBottom: 4 }}>
        <Avatar size="sm" src={authorAvatar} name={authorName} borderColor="blue.600" border="2px" />
      </div>
      <span css={{ color: "gray.700", marginLeft: 10, fontSize: fontSize, fontWeight: 600 }}>{authorName}</span>
      {typeof authorTitle !== "undefined" && authorTitle && authorTitle.length > 0 && (
        <span css={{ marginRight: 5 }}>, </span>
      )}
      <span css={{ color: "gray.600", fontSize: fontSize - 2, fontWeight: 400 }}>{authorTitle}</span>
    </div>
  );
}

function getDividerProps(showDivider: boolean) {
  if (showDivider) {
    return {
      borderBottom: "1px",
      borderColor: "gray.100",
    };
  }

  return null;
}
